import api from './api';
import { ApiResponse, PaginatedResponse } from '../types';

export interface Affiliate {
  id: string;
  user_id: string;
  referral_code: string;
  commission_percentage: number;
  status: 'pending' | 'approved' | 'rejected' | 'suspended';
  total_earnings: number;
  pending_earnings: number;
  paid_earnings: number;
  bank_info?: BankInfo;
  createdAt: string;
  updatedAt: string;
  user?: {
    id: string;
    email: string;
    first_name: string;
    last_name: string;
    company_name?: string;
  };
}

export interface BankInfo {
  bank_name?: string;
  account_type?: string;
  account_number?: string;
  routing_number?: string;
  pix_key?: string;
  paypal_email?: string;
}

export interface Referral {
  id: string;
  affiliate_id: string;
  referred_user_id: string;
  whitelabel_config_id?: string;
  status: 'pending' | 'converted' | 'churned';
  converted_at?: string;
  createdAt: string;
  referredUser?: {
    id: string;
    email: string;
    first_name: string;
    last_name: string;
    createdAt: string;
  };
}

export interface Commission {
  id: string;
  affiliate_id: string;
  referral_id: string;
  subscription_id: string;
  amount: number;
  percentage: number;
  status: 'pending' | 'approved' | 'paid';
  paid_at?: string;
  createdAt: string;
  referral?: Referral;
  affiliate?: Affiliate;
  subscription?: {
    id: string;
    plan?: {
      id: string;
      name: string;
      price: number;
    };
  };
}

export interface AffiliateStats {
  referral_code: string;
  commission_percentage: number;
  total_referrals: number;
  converted_referrals: number;
  conversion_rate: number;
  total_commissions: number;
  pending_commissions: number;
  approved_commissions: number;
  paid_commissions: number;
  total_earnings: number;
  pending_earnings: number;
  paid_earnings: number;
}

export interface ReferralCodeCheck {
  valid: boolean;
  affiliate_name?: string;
}

interface ListFilters {
  page?: number;
  limit?: number;
  status?: string;
}

export const affiliateService = {
  // User endpoints
  async getStatus(): Promise<ApiResponse<Affiliate | null>> {
    const response = await api.get('/affiliate/status');
    return response.data;
  },

  async apply(): Promise<ApiResponse<Affiliate>> {
    const response = await api.post('/affiliate/apply');
    return response.data;
  },

  async getStats(): Promise<ApiResponse<AffiliateStats>> {
    const response = await api.get('/affiliate/stats');
    return response.data;
  },

  async getReferrals(filters?: ListFilters): Promise<PaginatedResponse<Referral>> {
    const response = await api.get('/affiliate/referrals', { params: filters });
    return response.data;
  },

  async getCommissions(filters?: ListFilters): Promise<PaginatedResponse<Commission>> {
    const response = await api.get('/affiliate/commissions', { params: filters });
    return response.data;
  },

  async updateBankInfo(bank_info: BankInfo): Promise<ApiResponse<Affiliate>> {
    const response = await api.put('/affiliate/bank-info', { bank_info });
    return response.data;
  },

  // Public endpoint
  async checkReferralCode(code: string): Promise<ApiResponse<ReferralCodeCheck | null>> {
    const response = await api.get(`/affiliate/check/${encodeURIComponent(code)}`);
    return response.data;
  },

  // Admin endpoints
  async adminGetAffiliates(filters?: ListFilters & { search?: string }): Promise<PaginatedResponse<Affiliate>> {
    const response = await api.get('/admin/affiliates', { params: filters });
    return response.data;
  },

  async adminGetAffiliate(id: string): Promise<ApiResponse<Affiliate & { stats: AffiliateStats }>> {
    const response = await api.get(`/admin/affiliates/${id}`);
    return response.data;
  },

  async adminApproveAffiliate(id: string): Promise<ApiResponse<Affiliate>> {
    const response = await api.put(`/admin/affiliates/${id}/approve`);
    return response.data;
  },

  async adminRejectAffiliate(id: string): Promise<ApiResponse<Affiliate>> {
    const response = await api.put(`/admin/affiliates/${id}/reject`);
    return response.data;
  },

  async adminSuspendAffiliate(id: string): Promise<ApiResponse<Affiliate>> {
    const response = await api.put(`/admin/affiliates/${id}/suspend`);
    return response.data;
  },

  async adminReactivateAffiliate(id: string): Promise<ApiResponse<Affiliate>> {
    const response = await api.put(`/admin/affiliates/${id}/reactivate`);
    return response.data;
  },

  async adminUpdateCommissionRate(id: string, commission_percentage: number): Promise<ApiResponse<Affiliate>> {
    const response = await api.put(`/admin/affiliates/${id}/commission`, { commission_percentage });
    return response.data;
  },

  async adminGetAffiliateReferrals(id: string, filters?: ListFilters): Promise<PaginatedResponse<Referral>> {
    const response = await api.get(`/admin/affiliates/${id}/referrals`, { params: filters });
    return response.data;
  },

  async adminGetCommissions(filters?: ListFilters & { affiliate_id?: string }): Promise<PaginatedResponse<Commission>> {
    const response = await api.get('/admin/affiliates/commissions/all', { params: filters });
    return response.data;
  },

  async adminApproveCommission(id: string): Promise<ApiResponse<Commission>> {
    const response = await api.put(`/admin/affiliates/commissions/${id}/approve`);
    return response.data;
  },

  async adminMarkCommissionsAsPaid(commission_ids: string[]): Promise<ApiResponse<{ success: string[]; failed: { id: string; error: string }[] }>> {
    const response = await api.post('/admin/affiliates/commissions/pay', { commission_ids });
    return response.data;
  }
};
